import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { AgriSolutionState, GeneralState } from "../Redux";
import { useIsMobile, useTheme } from "../Hooks";
import Router from "../Pages";
import { FmLoader, FmApiResponseErrorModal } from "../Components";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { ApiError } from "../Models";
import { generalSlice } from "../Redux/States/General/GeneralSlice";

const Root = () => {
    const dispatch = useDispatch();
    const language = useSelector(GeneralState.Selectors.selectActiveLanguage);
    const theme = useTheme();
    const { i18n } = useTranslation();
    const appIsReady = useSelector(GeneralState.Selectors.selectAppIsReady);
    const showMapInMobile = useSelector(AgriSolutionState.Selectors.selectShowMobileMap);
    const apiErrorsState = useSelector(GeneralState.Selectors.selectApiErrors);
    const isMobile = useIsMobile();
    const activeLanguage = useSelector(GeneralState.Selectors.selectActiveLanguage);
    /* Add overflow hidden to body when needed */
    useEffect(() => {
        let hasOverFlow = true;
        if (isMobile && showMapInMobile) {
            hasOverFlow = false;
        }
        document.body.style.overflow = hasOverFlow ? "auto" : "hidden";
    }, [showMapInMobile, isMobile]);

    useEffect(() => {
        const initApp = async () => {
            await dispatch(GeneralState.Thunks.initApp());
        };

        initApp();
        const errorsModel: ApiError = {
            showGeneralErrorModal: false,
            statusCode: 0,
            serviceName: "",
            errorMessage: "",
        };
        dispatch(GeneralState.Actions.setApiErrors(errorsModel));
    }, []);

    // /* This is temporary code to change language */
    useEffect(() => {
        console.log("useEffect root");
        dispatch(generalSlice.actions.setLanguage(activeLanguage));
    }, []);

    const handleCloseErrorModal = () => {
        const errorsModel: ApiError = {
            showGeneralErrorModal: false,
            statusCode: 0,
            serviceName: "",
            errorMessage: "",
        };
        dispatch(GeneralState.Actions.setApiErrors(errorsModel));
    };
    /* Set app state to ready after a short period of time to allow "language swap" without flickering
     NOTE: this is hardcoded, and not the best solution.
     TODO: fix this to be inside change language callback instead of half a second hardcoded timeout
      + Add a loader */
    const [uiIsReady, setUiIsReady] = useState<boolean>(false);

    useEffect(() => {
        const timeout = setTimeout(() => setUiIsReady(true), 150);
        return () => timeout && clearTimeout(timeout);
    }, []);

    useEffect(() => {
        i18n.changeLanguage(language);
    }, [language]);

    const showLoader = !uiIsReady || !appIsReady;

    const isRtl = theme.direction === "rtl" ? true : false;

    const cacheRtl = createCache({
        key: isRtl ? "muirtl" : "muiltr",
        stylisPlugins: isRtl ? [rtlPlugin] : [],
    });

    return (
        <ThemeProvider theme={theme}>
            <CacheProvider value={cacheRtl}>
                <FmApiResponseErrorModal
                    isOpen={apiErrorsState && apiErrorsState?.showGeneralErrorModal ? apiErrorsState?.showGeneralErrorModal : false}
                    onClose={handleCloseErrorModal}
                    data={apiErrorsState}
                />
                <CssBaseline />
                {!showLoader && <Router />}
                {showLoader && <FmLoader size={"big"} fullScreenCover />}
            </CacheProvider>
        </ThemeProvider>
    );
};

export default Root;
